/**
 * @file src/room/roomDeckBridge.js
 * @description The one narrow seam between the two owners of a synced DECK load: the collection app
 * (owns the decks — it actually loads a pool card onto a stage) and the room client (owns the wire —
 * it stamps and relays the load protocol). No-window-globals module singleton, the same pattern the
 * room store / roster use.
 *
 * A deck load is owner-gated, and TWO-PHASE whenever it rebuilds the deck's audio engine (a track
 * deal or an orbiter swap; a world swap is visuals-only and ships single-shot, no barrier):
 *  1. The deck's owner publishes a load COMMAND (`/voice/<id>/load`, carrying a `loadId` when the
 *     barrier applies). Every client — including spectators — loads the card locally through the same
 *     manual card-load/swap path.
 *  2. Each client publishes a `load-ready` ack when its local load finishes. The deck stays "arming"
 *     (not playable) until every SLOT-HOLDING player has acked that `loadId`; spectators load
 *     best-effort, outside the barrier. A failed load or a timeout releases the barrier with a warning.
 *
 * Directions across this seam:
 *  - `publishDeckLoad` (collection app → wire): the local owner deals a card; returns the minted loadId.
 *  - `publishDeckClear` (collection app → wire): the local owner ejects the deck (empties everywhere).
 *  - `applyRemoteDeckLoad` (wire → collection app): a stamped command re-runs the manual path locally.
 *  - `applyRemoteDeckClear` (wire → collection app): a stamped clear (or snapshot's empty deck) applies.
 *  - `notifyDeckLoaded` / `markDeckLoadFailed` (collection app → wire): the local load finished / failed.
 *  - `deckContents` (wire read): current decks for a `player:state` snapshot (late-joiner materialize).
 *  - `deckIsMaterializing` (wire read): is this deck's card mid-load right now? A snapshot must say
 *    nothing about such a deck — see below.
 *  - `applyRemoteCardPlayed` (wire → collection app): a stamped load COMMAND played this card.
 *  - `handConsumed` (wire read) / `applyRemoteHandConsumed` (wire → collection app): which of the
 *    section's dealt cards have already been played, rides the same snapshot (see below).
 *
 * An entry reference is minimal — one id field naming the card's entity kind (`{ trackId, version? }`
 * / `{ orbiterId, version? }` / `{ entangledWorldId, version? }`) — enough for any client to resolve
 * the same pool card (the pool is the grant-pinned collection, identical for everyone) and apply it.
 *
 * A snapshot has no way to say "loading". It says a card, or it says nothing — and nothing MEANS
 * empty: a peer receiving a deck-less snapshot for an owned deck empties its own copy, which is how
 * a client that missed a clear converges. That makes a deck this client is still MATERIALIZING
 * unsayable: read from settled state alone it looks empty, and publishing it tells everyone the
 * owner ejected a card that is in fact on its way in. On a rejoin every owned deck passes through
 * that state at once, so the reloading player's decks cut out on everyone else's screen and come
 * back only when the load lands. So the snapshot SKIPS a deck that is materializing: the room keeps
 * the picture it already has, and the settled state follows a moment later — which is exactly how a
 * mapped-but-still-booting voice was already treated.
 *
 * A played card leaves the hand, and every client must agree on which ones are gone. The load
 * COMMANDS already carry that: each one is relayed to everybody, so a card played anywhere is
 * consumed everywhere with nothing extra on the wire. Which is why `applyRemoteCardPlayed` is its
 * own direction rather than something the load applier infers: the applier ALSO runs for deck
 * snapshots, and a snapshot describes what a deck is holding, not what was just played — it can name
 * a card dealt two sections ago, and treating that as a play would strike it out of a hand that has
 * legitimately re-offered it, on the receiving clients only.
 *
 * What the commands cannot reach is a client that was not listening — a late joiner, or one
 * repairing a gap — and the relay keeps exactly one thing for those: the last `player:state` per
 * deck. So the consumed set rides IN that snapshot, tagged with the deal it belongs to. It is the
 * whole hand's set, not the publishing deck's share of it: taking the UNION of the snapshots whose
 * tag matches the live deal makes the repair idempotent and order-free, and needs no per-deck
 * attribution to be correct.
 */

// Room-client handlers (set by the room client):
let publish = null; // (stageIndex, entryRef) => loadId|null — mint + send the load command
let publishClear = null; // (stageIndex) => void — send the deck CLEAR command (load [null])
let notifyLoaded = null; // (stageIndex, loadId) => void — the local load of loadId finished → ack
let notifyFailed = null; // (stageIndex, loadId) => void — the local load of loadId failed → surface
// Collection-app handlers (set by the collection app):
let applier = null; // (stageIndex, entryRef, loadId) => void — apply a remote load locally (no echo)
let clearApplier = null; // (stageIndex) => void — apply a remote deck clear locally (no echo)
let contentsProvider = null; // () => Array<entryRef|null> by stage index
let pendingProvider = null; // (stageIndex) => boolean — is that deck's card mid-load right now?
let handProvider = null; // () => {deal:string, consumed:string[]}|null — the hand's played cards
let handApplier = null; // (snapshot) => void — fold a snapshot's played cards into the local hand
let playedApplier = null; // (entryRef) => void — a stamped load COMMAND played this card

/** Collection app → wire: a local owned-deck load. Returns the minted loadId (null outside room mode). */
export function publishDeckLoad(stageIndex, entryRef) {
  return publish?.(stageIndex, entryRef) ?? null;
}

/** Collection app → wire: a local owned-deck CLEAR (the eject control) — the deck empties everywhere. */
export function publishDeckClear(stageIndex) {
  publishClear?.(stageIndex);
}

/** Collection app → wire: the local load of `loadId` on this deck finished — publish our readiness ack. */
export function notifyDeckLoaded(stageIndex, loadId) {
  notifyLoaded?.(stageIndex, loadId);
}

/** Collection app → wire: the local load of `loadId` failed — surface it (release the barrier + warn). */
export function markDeckLoadFailed(stageIndex, loadId) {
  notifyFailed?.(stageIndex, loadId);
}

/** Wire → collection app: apply a stamped remote load through the manual card-load path (no echo). */
export function applyRemoteDeckLoad(stageIndex, entryRef, loadId) {
  applier?.(stageIndex, entryRef, loadId);
}

/** Wire → collection app: apply a stamped remote deck CLEAR (or a snapshot's empty deck) locally. */
export function applyRemoteDeckClear(stageIndex) {
  clearApplier?.(stageIndex);
}

/** The current deck contents by stage index (entryRef or null), for `player:state` snapshots. */
export function deckContents() {
  return contentsProvider?.() ?? [];
}

/** Is this deck's card still materializing on THIS client — dealt or resynced, not yet settled? A
 *  snapshot must stay silent about such a deck rather than describe it as empty (see the header). */
export function deckIsMaterializing(stageIndex) {
  return pendingProvider?.(stageIndex) === true;
}

/** The live hand's played cards + the deal they belong to, for `player:state` snapshots. Null when
 *  this room deals no hand — then nothing is consumed and the snapshot says nothing about it. */
export function handConsumed() {
  return handProvider?.() ?? null;
}

/** Wire → collection app: fold a snapshot's played cards into the local hand (union, same deal only). */
export function applyRemoteHandConsumed(snapshot) {
  handApplier?.(snapshot);
}

/** Wire → collection app: a stamped load COMMAND — this card has just been played out of the hand.
 *  Only ever called for a real command; a deck snapshot must not reach it (see the file header). */
export function applyRemoteCardPlayed(entryRef) {
  playedApplier?.(entryRef);
}

/** The room client registers how load/clear commands reach the wire and how local completion acks. */
export function setRoomDeckHandlers({
  publish: p = null, publishClear: pc = null, notifyLoaded: nl = null, notifyFailed: nf = null,
} = {}) {
  publish = typeof p === 'function' ? p : null;
  publishClear = typeof pc === 'function' ? pc : null;
  notifyLoaded = typeof nl === 'function' ? nl : null;
  notifyFailed = typeof nf === 'function' ? nf : null;
}

/** The collection app registers how a remote deck load is applied locally. */
export function setDeckLoadApplier(fn) {
  applier = typeof fn === 'function' ? fn : null;
}

/** The collection app registers how a remote deck CLEAR is applied locally. */
export function setDeckClearApplier(fn) {
  clearApplier = typeof fn === 'function' ? fn : null;
}

/** The collection app registers the read of live deck contents (for snapshots). */
export function setDeckContentsProvider(fn) {
  contentsProvider = typeof fn === 'function' ? fn : null;
}

/** The collection app registers the read of which decks are mid-load (for snapshots). */
export function setDeckPendingProvider(fn) {
  pendingProvider = typeof fn === 'function' ? fn : null;
}

/** The collection app registers how the hand's played cards are read for a snapshot, how a
 *  snapshot's are folded back in, and how a remote load command plays one. */
export function setHandConsumedHandlers({ read = null, apply = null, played = null } = {}) {
  handProvider = typeof read === 'function' ? read : null;
  handApplier = typeof apply === 'function' ? apply : null;
  playedApplier = typeof played === 'function' ? played : null;
}

/** Drop every registration (teardown / tests). */
export function resetDeckBridge() {
  publish = null;
  publishClear = null;
  notifyLoaded = null;
  notifyFailed = null;
  applier = null;
  clearApplier = null;
  contentsProvider = null;
  pendingProvider = null;
  handProvider = null;
  handApplier = null;
  playedApplier = null;
}
